import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { createGlobalStyle } from 'styled-components';
import Login from './components/Login/Login';
import Dashboard from './components/Dashboard/Dashboard';
import SettingsPage from './components/Dashboard/settings/SettingsPage';
import ProtectedRoute from './components/ProtectedRoute';
import { checkAuthStatus, setInitialLoadComplete } from './store/authSlice';

// Global styles
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background: #f8fafc;
    color: #1a202c;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    font-family: inherit;
  }

  input {
    font-family: inherit;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
`;

// Component to handle login redirects properly
const LoginWrapper = () => {
  const location = useLocation();
  const { isAuthenticated } = useSelector(state => state.auth);
  
  // If user is authenticated, redirect to the intended destination or dashboard
  if (isAuthenticated) {
    const from = location.state?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }
  
  return <Login />;
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, loading, authChecked, initialLoad } = useSelector(state => state.auth);

  // Debug logging
  console.log('App render:', { isAuthenticated, loading, authChecked, initialLoad, currentPath: window.location.pathname });

  // Check authentication status on app startup
  useEffect(() => {
    console.log('App useEffect triggered:', { authChecked, initialLoad, loading });
    
    // Only check if we haven't checked auth yet and this is the initial load
    if (!authChecked && initialLoad) {
      // Check if we have any tokens in localStorage
      const hasToken = localStorage.getItem('authToken');
      console.log('Token check:', { hasToken: !!hasToken, token: hasToken ? 'present' : 'missing' });
      
      if (hasToken) {
        console.log('Found token in localStorage, checking auth status...');
        dispatch(checkAuthStatus());
      } else {
        console.log('No token found, skipping auth check');
        dispatch(setInitialLoadComplete());
      }
    }
  }, [dispatch, authChecked, initialLoad]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <>
        <GlobalStyle />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#667eea'
        }}>
          Loading...
        </div>
      </>
    );
  }

  return (
    <>
      <GlobalStyle />
      <Router>
        <AppContainer>
          <Routes>
            <Route 
              path="/login" 
              element={<LoginWrapper />}
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/" 
              element={
                <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
              } 
            />
            <Route 
              path="*" 
              element={
                <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />
              } 
            />
          </Routes>
        </AppContainer>
      </Router>
    </>
  );
}

export default App;
